import React, { MouseEvent, ReactElement, useEffect, useState } from 'react';
import Table from 'react-bootstrap/Table';
import { useTranslation } from 'react-i18next';
import { HeaderGroup, Row, ColumnInstance, TableHeaderProps } from 'react-table';
import { FaArrowAltCircleDown, FaArrowAltCircleUp } from 'react-icons/fa';

import { useModal } from 'store/modal';
import { useQueryClient } from 'react-query';

import LeavesMobileTable from './leaves/LeavesMobileTable';
import DocsTable from './documents/DocsTable';
import LogsTable from './documents/LogsTable';
import TableLoader from './TableLoader';
import { SetSortParamType } from './FetchTable';

export interface IProps<T extends Record<string, unknown>> {
  loading: boolean;
  getTableProps: () => Record<string, any>;
  headerGroups: Array<HeaderGroup<T>>;
  getTableBodyProps: () => Record<string, any>;
  toggleAllRowsExpanded?: (isExpanded?: boolean) => void;
  rows: Array<Row<T>>;
  visibleColumns: ColumnInstance<T>[];
  prepareRow: (x: Row<T>) => void;
  className?: string;
  error?: string | null;
  entityName?: string;
  docTab?: string;
  expandAllRows?: boolean;
  isExpandable?: boolean;
  isLeavesMobile?: boolean;
  setSortParam?: SetSortParamType;
  setSortParamRef?: SetSortParamType;
  useBackendSorting?: boolean;
}

/*
 * `TableComponent` implements table skeleton and all columns/rows related options.
 */
function TableComponent<T extends Record<string, unknown>>({
  loading,
  getTableProps,
  headerGroups,
  getTableBodyProps,
  toggleAllRowsExpanded,
  rows,
  visibleColumns,
  prepareRow,
  className,
  error,
  entityName,
  docTab,
  expandAllRows,
  isExpandable,
  isLeavesMobile,
  setSortParam,
  setSortParamRef,
  useBackendSorting,
}: IProps<T>): ReactElement {
  const { t } = useTranslation();
  const { showModal, hideModal } = useModal();
  const [rowId, setRowId] = useState<string | null>(null);
  const [hoveredRow, setHoveredRow] = useState<number | null>(null);
  const windowInnerWidth = window.innerWidth;
  const isMobile = windowInnerWidth < 768;
  const queryClient = useQueryClient();

  /**
   * If all rows should be expanded by default
   * make sure that we can toggle them as well, so expand rows
   * only on rows data update
   */
  useEffect(() => {
    if (expandAllRows) {
      const firstRowId: string = rows[0]?.original.id as string;
      if (firstRowId !== rowId) {
        setRowId(firstRowId);
        if (toggleAllRowsExpanded) toggleAllRowsExpanded(true);
      }
    }
  }, [rows]);

  useEffect(() => {
    if (expandAllRows !== undefined) {
      // Make expanding all rows toggle available
      if (toggleAllRowsExpanded) toggleAllRowsExpanded(expandAllRows);
    }
  }, [expandAllRows]);

  const printSortSymbol = (isSortedDesc: boolean | undefined) =>
    isSortedDesc ? (
      <FaArrowAltCircleDown
        size={20}
        style={{ marginLeft: 6 }}
      />
    ) : (
      <FaArrowAltCircleUp
        size={20}
        style={{ marginLeft: 6 }}
      />
    );

  const renderData = () => {
    if (loading) {
      return (
        <tr>
          <td
            colSpan={headerGroups[0].headers.length}
            className="text-center"
          >
            <TableLoader />
          </td>
        </tr>
      );
    }

    if (error)
      return (
        <tr>
          <td
            colSpan={headerGroups[0].headers.length}
            className="text-center"
          >
            {error}
          </td>
        </tr>
      );

    if (rows.length < 1)
      return (
        <tr>
          <td
            colSpan={headerGroups[0].headers.length}
            className="text-center"
          >
            {t('table.noResults')}
          </td>
        </tr>
      );

    // Loop over the table rows
    return rows.map((row, i) => {
      const rowKey: number = row.original.id as number;
      // Prepare the row for display
      prepareRow(row);
      const prepareSubRow = renderRowSubComponent({ row });
      return (
        // Apply the row props
        <React.Fragment key={rowKey || i}>
          <tr
            {...row.getRowProps()}
            {...(isExpandable && row.getToggleRowExpandedProps())}
            onMouseEnter={() => setHoveredRow(rowKey)}
            onMouseLeave={() => setHoveredRow(null)}
            className={`${row.isExpanded ? 'c-row--expanded' : ''} ${
              hoveredRow === rowKey && isLeavesMobile ? 'hover-style' : ''
            }`}
          >
            {row.cells.map(cell => (
              <td {...cell.getCellProps()}>{cell.render('Cell')}</td>
            ))}
          </tr>
          {isLeavesMobile && isMobile && (
            <LeavesMobileTable
              data={row.original}
              onMouseEnter={() => setHoveredRow(rowKey)}
              onMouseLeave={() => setHoveredRow(null)}
              className={hoveredRow === rowKey ? 'hover-style' : ''}
            />
          )}
          {/*
            If the row is in an expanded state, render a row with a
            column that fills the entire length of the table.
          */}
          {row.isExpanded && !!prepareSubRow ? (
            <tr className="c-row--expanded__content">
              <td colSpan={visibleColumns.length}>
                <div
                  className="sr-only"
                  data-number={(row?.original?.documents as any[])?.[0]?.number ?? ''}
                >
                  {(row?.original?.documents as any[])?.[0]?.number ?? ''}
                </div>
                {/*
                    Inside it, call our renderRowSubComponent function. In reality,
                    you could pass whatever you want as props to
                    a component like this, including the entire
                    table instance. But for this example, we'll just
                    pass the row
                  */}
                {prepareSubRow}
              </td>
            </tr>
          ) : null}
        </React.Fragment>
      );
    });
  };

  // Create a function that will render our row sub components
  const renderRowSubComponent = React.useCallback(({ row }: { row: Row<any> }) => {
    if (row.original.documents)
      return (
        <>
          <DocsTable
            data={row.original.documents}
            totalCount={row.original.documents?.length}
            entityName={entityName || ''}
            docTab={docTab}
            isMultiUser={row.original.is_multi_user}
            categoryName={row.original.category_name}
            createdAt={row.original.created_at}
            holderName={row.original.name}
            allDocumentButtons={row.original.buttons}
          />
          {isMobile && (
            <hr
              className="mb-0 mt-0"
              style={{ borderTop: '1px solid black' }}
            />
          )}
        </>
      );
    if (row.original.logs)
      return (
        <LogsTable
          data={row.original.logs}
          totalCount={row.original.logs.length}
        />
      );
  }, []);

  return (
    <Table
      hover
      responsive
      className={className}
      {...getTableProps()}
    >
      <thead className="thead-light">
        {
          // Loop over the header rows
          headerGroups.map(headerGroup => (
            // Apply the header row props
            <tr {...headerGroup.getHeaderGroupProps()}>
              {
                // Loop over the headers in each row
                headerGroup.headers.map((column: HeaderGroup<T>) => {
                  const { onClick, ...props } = column.getHeaderProps(
                    column.getSortByToggleProps()
                  ) as TableHeaderProps & { onClick: (e: MouseEvent<HTMLTableCellElement>) => void };
                  return (
                    <th
                      onClick={(e: MouseEvent<HTMLTableCellElement>) => {
                        setTimeout(() => {
                          if (onClick) {
                            onClick(e);
                          }
                          if (useBackendSorting) {
                            const { id, isSorted, isSortedDesc, canSort } = column;

                            const sortFieldName = id === 'name' ? 'first_name' : id;

                            const sortOrder = isSorted ? `sort[0][order]=${isSortedDesc ? 'desc' : 'asc'}` : '';
                            const sortName = isSorted ? `sort[0][name]=${sortFieldName}` : '';

                            if (setSortParam && setSortParamRef && isSorted) {
                              setSortParam(`${sortName}&${sortOrder}`);
                              setSortParamRef(`${sortName}&${sortOrder}`);
                            }
                            if (setSortParam && setSortParamRef && !isSorted) {
                              setSortParam(null);
                              setSortParamRef(null);
                            }

                            if (canSort) {
                              queryClient.invalidateQueries(entityName);
                            }
                          }
                        });
                      }}
                      // Apply the header cell props
                      {...props}
                      style={{
                        minWidth: column.minWidth,
                        maxWidth: column.maxWidth,
                        width: column.width,
                        flex: 0,
                        userSelect: 'none',
                      }}
                      title={t('table.sort')}
                    >
                      {
                        // Render the header
                        column.render('Header')
                      }
                      {/* Apply sort marker */}
                      <span>{column.isSorted ? printSortSymbol(column.isSortedDesc) : ''}</span>
                    </th>
                  );
                })
              }
            </tr>
          ))
        }
      </thead>
      {/* Apply the table body props */}
      <tbody {...getTableBodyProps()}>{renderData()}</tbody>
    </Table>
  );
}

export default TableComponent;
